import { AirportAutocomplete } from "@airhelp/react-ui-components";
import type { Airport as AirportProps } from "@airhelp/react-ui-components";
import { useCallback, useState, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import debounce from "lodash.debounce";
import { getAirports } from "services/webapp";
import { trackSearchEvent } from "utils/tracking";
import type { AirportOption, AutocompleteInputProps } from "./AutocompleteInput.d";

const updateAirports = debounce((query: string, setAirports: Dispatch<SetStateAction<AirportProps[]>>, instanceId: string) => {
  getAirports(query).then((airports) => {
    setAirports(airports);

    const airportName = airports[0]?.name ?? "empty";
    const searchType = instanceId === "departure-input" ? "departure" : "destination";
    trackSearchEvent({ event: "search_airport", type: searchType, searchPhrase: query, label: airportName });
  });
}, 300);

export const AutocompleteInput = ({
  autoFocus,
  placeholder,
  setAirportSelected,
  instanceId,
  dataTestId,
  icon,
  activeAirport,
}: AutocompleteInputProps) => {
  const [airports, setAirports] = useState<AirportProps[]>([]);

  const updateDropdownList = useCallback((query: string) => updateAirports(query, setAirports, instanceId), [instanceId]);

  const airportOptions = useMemo(
    () => airports?.map((airport) => ({ value: airport, isDisabled: airport.name === activeAirport?.name })),
    [activeAirport, airports]
  );

  const handleInputChange = (inputValue: string) => {
    if (!inputValue) return setAirports([]);

    return updateDropdownList(inputValue);
  };

  const handleAirportChange = (option: AirportOption) => setAirportSelected(option?.value);

  return (
    <AirportAutocomplete
      menuIsOpen={airportOptions?.length > 0}
      options={airportOptions}
      onInputChange={handleInputChange}
      autoFocus={autoFocus}
      placeholder={placeholder}
      handleChange={handleAirportChange}
      instanceId={instanceId}
      dataTestId={dataTestId}
      aria-label={placeholder.toString()}
      icon={icon}
      tabSelectsValue
    />
  );
};
